import React from 'react'
import { isChunkLoadError } from '../../utils/lazyWithRetry'

/*
 * Without a boundary, any error while rendering left the patient on a white page
 * until they refreshed (QA: the chat page blank after switching browser tabs).
 *
 * A screen whose code failed to load (lazyWithRetry already retried it) reloads
 * the page once, which fetches the current build. Anything else, or a second
 * chunk failure soon after that reload, shows a short message with a Reload
 * button instead of a blank page.
 */
export const RELOAD_KEY = 'ydo_chunk_reload_at'
export const RELOAD_WINDOW_MS = 30 * 1000

/** True at most once per RELOAD_WINDOW_MS, so a reload can never loop. */
export const shouldReloadForChunkError = (now = Date.now()) => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0)
    if (now - last < RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(RELOAD_KEY, String(now))
    return true
  } catch (e) {
    return false
  }
}

const styles = {
  box: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  title: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
  text: { fontSize: 15, color: '#555', marginBottom: 20, maxWidth: 420 },
  button: {
    padding: '10px 28px',
    borderRadius: 6,
    border: 'none',
    background: '#1f6feb',
    color: '#fff',
    fontSize: 15,
    cursor: 'pointer',
  },
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary', error, info?.componentStack)
    if (isChunkLoadError(error) && shouldReloadForChunkError()) {
      window.location.reload()
    }
  }

  render() {
    const { error } = this.state
    const { children } = this.props
    if (!error) return children
    return (
      <div style={styles.box} role="alert">
        <div style={styles.title}>Something went wrong</div>
        <div style={styles.text}>
          Something didn&apos;t load properly. Please reload the page to
          continue.
        </div>
        <button type="button" style={styles.button} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    )
  }
}

export default AppErrorBoundary
